import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import NotesOutlinedIcon from '@material-ui/icons/NotesOutlined'
import SearchIcon from '@material-ui/icons/Search'
import Pagination from '@material-ui/lab/Pagination'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  addIssueNote,
  connectTim,
  fetchIssue,
  fetchIssueCategories,
  fetchIssueFormLinks,
  fetchIssueNotes,
  fetchIssues,
  fetchScopedCentres,
  updateIssue
} from 'api'

/**
 * Issues — the board that replaces the "Central Issue Tracker V2" sheet.
 *
 * The sheet had one tab per team plus a master roll-up that had silently broken
 * (#VALUE!), so nobody could see across teams at all. This is one list with
 * filters instead: an owner filters to their category, a centre to their centre,
 * HQ leaves it alone.
 *
 * Age reads on sight — two blockers sitting untouched for 27 and 58 days is
 * exactly what the spreadsheet never surfaced. It is ALSO sortable now (user,
 * 2026-09-10: "we should be able to sort the age, oldest 1st or sooner ones
 * 1st"). Sorting happens in the browser because this endpoint is not
 * paginated: every row the filters allow is already here, so a sort costs a
 * comparison rather than a round trip, and it cannot disagree with the tab
 * counts the way a server-side ordering over a page of results would.
 */
// 'All' leads and is the landing tab (user, 2026-08-18). Opening on "To be
// picked" answered "what is unclaimed?" before anyone had asked it, and hid
// the in-progress pile — the sheet's failure was never being able to see
// across, so the board opens on everything and you narrow from there.
// `value: 'all'` is what the API already treats as "no status filter".
const STATUS_TABS = [
  { value: 'all', label: 'All', countKey: 'all' },
  { value: 'to_be_picked', label: 'To be picked', countKey: 'to_be_picked' },
  { value: 'in_progress', label: 'In progress', countKey: 'in_progress' },
  { value: 'resolved', label: 'Resolved', countKey: 'resolved' }
]

// The dialog's status dropdown (2026-09-22, centre item i: "rename 'start on
// it' to a dropdown of the actual status"). Same three words as the tabs.
const STATUS_OPTIONS = [
  { value: 'to_be_picked', label: 'To be picked' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' }
]

// Under the status chip: the centre's answer to "is it really fixed?"
const CONFIRM_SHORT = {
  awaiting: 'awaiting centre',
  confirmed: 'centre confirmed',
  reopened: 'centre reopened',
  no_reply: 'centre silent'
}

const STATUS_STYLE = {
  to_be_picked: { background: '#fbf0dc', color: '#a4580a' },
  in_progress: { background: '#e5edfb', color: '#1e4fa3' },
  resolved: { background: '#e3f2e7', color: '#196b3a' }
}

// Centre scoping matches Operations/IncidentReports: the picker lives in the
// page header, the choice sticks between visits, and a centre-mapped user never
// sees an "all centres" option that would only ever return an empty list.
// Breathing room under the table so the last row is not flush to the window.
const BOTTOM_GUTTER = 24

const ALL_CENTRES = 'all'
const CENTRE_KEY = 'oh.issueTracker.centre'

// Label row height, shared by the sticky offset below so the two cannot drift.
const HEAD_LABEL_HEIGHT = 32

// An issue open this long is the reason to look at this page at all.
const STALE_DAYS = 14

const useStyles = makeStyles((theme) => ({
  // Header + filter geometry matches Operations/IncidentReports so the two
  // pages read as the same product: title (+ how-this-works) left, filters and
  // the primary action right.
  root: { padding: theme.spacing(3) },
  head: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  filters: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2)
  },
  tab: {
    borderRadius: 6,
    padding: theme.spacing(0.75, 1.5),
    fontSize: 14,
    cursor: 'pointer',
    userSelect: 'none',
    border: '1px solid transparent'
  },
  tabOn: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600
  },
  tabOff: { color: '#5f5a52', border: '1px solid #e3ddd3' },
  count: { opacity: 0.65, marginLeft: 6 },
  filter: { minWidth: 170 },
  // Sticky needs a bounded scroll container, or there is nothing to stick to.
  // The bound is measured at runtime rather than guessed with a calc(), which
  // left the table ending well short of the bottom of the window.
  tableWrap: { overflowY: 'auto' },
  // stickyHeader pins each head cell at top:0. Both header rows need an opaque
  // background of their own — MUI's default leaves them translucent and the
  // rows scrolling underneath show through. The filter row also needs pushing
  // down by the height of the label row above it, or the two overlap.
  // The filter row's `top` must equal the label row's real height or the two
  // overlap, so the label row is pinned to a fixed height rather than left to
  // MUI's padding (which measured 56px, not the 33 first assumed).
  labelRow: {
    '& .MuiTableCell-stickyHeader': {
      height: HEAD_LABEL_HEIGHT,
      boxSizing: 'border-box',
      padding: '8px 16px 0',
      lineHeight: '20px',
      verticalAlign: 'top',
      borderBottom: 'none',
      background: theme.palette.background.paper
    }
  },
  filterRow: {
    '& .MuiTableCell-stickyHeader': {
      top: HEAD_LABEL_HEIGHT,
      background: theme.palette.background.paper,
      paddingTop: 0,
      paddingBottom: 8
    }
  },
  headFilter: { maxWidth: '100%' },
  centre: { minWidth: 200 },
  linkUrl: { wordBreak: 'break-all', flex: '1 1 auto' },
  dialogField: { minWidth: 220, '& select': { padding: '6px 28px 6px 10px' } },
  // Fixed layout, because a long category ('Program Experience — Preschool,
  // Afterschool, PTC') overflowed its cell under auto layout and printed over
  // the next column. Fixed honours these widths and wraps instead.
  table: { tableLayout: 'fixed' },
  colIssue: { width: '35%' },
  colCentre: { width: '15%' },
  colCategory: { width: '24%' },
  colStatus: { width: '12%' },
  colAge: { width: '7%' },
  colNotes: { width: '7%' },
  // The per-row Notes button: icon + count, small enough to sit in a 7%
  // column, and its own click target so opening notes does not also open
  // the issue underneath it.
  notesBtn: {
    minWidth: 0,
    padding: '2px 8px',
    fontSize: 12.5,
    fontWeight: 600,
    color: '#5f5a52',
    borderColor: '#e3ddd3',
    '& .MuiButton-startIcon': { marginRight: 4 }
  },
  noteRow: {
    padding: theme.spacing(1, 0),
    borderBottom: '1px solid #f0ece6',
    '&:last-child': { borderBottom: 'none' }
  },
  noteText: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  // A uuid is 36 characters of noise to read but the whole point is copying it
  // into Slack search, so it gets a monospace face and `user-select: all` —
  // one click selects the entire string instead of a word of it.
  oldId: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 12,
    userSelect: 'all',
    wordBreak: 'break-all'
  },
  // Alfred's table styling sets white-space:nowrap, which no amount of
  // word-break can wrap against — that is why the long category names printed
  // over the column beside them. Re-enable wrapping for these cells.
  //
  // ⭐ The ISSUE cell needs it just as much, and got it on 2026-09-09. The bug
  // was invisible while the board only held issues typed into Alfred's own
  // form, which are short; the sheet bridge then imported titles written as
  // whole sentences (the column allows 255 chars) and they printed straight
  // over the Category column. `tableLayout: fixed` sizes the column correctly
  // either way — nowrap is what makes the text escape it.
  wrap: { whiteSpace: 'normal', wordBreak: 'break-word' },
  title: { fontWeight: 600, display: 'block' },
  sub: { color: '#8b837a', fontSize: 12.5 },
  chip: { fontWeight: 600, fontSize: 12 },
  stale: { color: '#d9481f', fontWeight: 700 },
  confirm: { display: 'block', color: '#8b837a', fontSize: 11.5, marginTop: 2 },
  num: { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  empty: { padding: theme.spacing(6), textAlign: 'center', color: '#8b837a' },
  // Sits under the scroll container, so it never scrolls away; the table's
  // measured height leaves room for it (see `measure`). Left-aligned, caption
  // then pager: the bottom-right corner belongs to the Ask Bruce button,
  // which sat on top of the last page number and the next arrow.
  foot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(3),
    minHeight: 40,
    paddingTop: theme.spacing(1)
  },
  dl: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px' },
  dt: { color: '#8b837a', fontSize: 12.5 },
  body: {
    borderLeft: '2px solid #e3ddd3',
    paddingLeft: theme.spacing(1.75),
    color: '#5f5a52',
    margin: theme.spacing(1.5, 0, 2),
    // Keep the raiser's own line breaks — "Parent number: …", "Child: …" on
    // their own lines read as a list, not one run-on paragraph (Satya,
    // 2026-09-28).
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  slackErr: {
    background: '#fdecea',
    color: '#a4231a',
    borderRadius: 6,
    padding: theme.spacing(1, 1.25),
    fontSize: 12.5,
    marginTop: theme.spacing(1.5)
  }
}))

const IssueTracker = () => {
  const classes = useStyles()

  const [rows, setRows] = useState([])
  // null = the API's own order (newest first, the model's `-opened_at`), which
  // stays the default: the board is a queue of what arrived, not a league
  // table. Clicking Age opts into oldest-first, clicking again flips it.
  const [ageSort, setAgeSort] = useState(null)
  const [counts, setCounts] = useState({})
  // The server's own answer to "is this board narrowed to what I own?".
  // Only used to word an empty result honestly — never to decide access.
  const [ownerScoped, setOwnerScoped] = useState(false)
  const [categories, setCategories] = useState([])
  const [centres, setCentres] = useState([])
  const [loading, setLoading] = useState(true)

  // Tim's "Connect my Alfred account" link lands here as ?tim_connect=<token>
  // (apps/oh_issues/services/tim.py). The board is the landing page because
  // every owner can already open it — no new route, no new permission.
  const [timConnect, setTimConnect] = useState(null)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('tim_connect')
    if (!token) return
    // Drop the token from the address bar so a refresh or a copied URL does
    // not replay it.
    params.delete('tim_connect')
    const rest = params.toString()
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (rest ? `?${rest}` : '')
    )
    setTimConnect({ state: 'working' })
    connectTim(token)
      .then((data) => setTimConnect({ state: 'done', name: data.connected_as }))
      .catch((error) =>
        setTimConnect({
          state: 'error',
          text:
            error?.response?.data?.detail ||
            'Could not connect. Message Tim again for a fresh link.'
        })
      )
  }, [])

  const [statusTab, setStatusTab] = useState('all')
  const [centre, setCentre] = useState(
    () => window.localStorage.getItem(CENTRE_KEY) || ALL_CENTRES
  )
  const [unrestricted, setUnrestricted] = useState(true)
  const [category, setCategory] = useState('all')
  const [search, setSearch] = useState('')
  // Typing hits the API, so debounce it — otherwise every keystroke
  // is a request and the results race each other back.
  //
  // The term and the page travel together: a new term always lands on page
  // 1, and keeping both in ONE state means one render and one request. Two
  // separate setStates from inside the debounce timer are not batched on
  // React 16 — two renders, two fetches, and the stale one can land last.
  const [query, setQuery] = useState({ term: '', page: 1 })
  // What the server actually served — its page, not the one asked for,
  // because it clamps a page number that no longer exists.
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1, size: 50 })
  const firstPage = (prev) => (prev.page === 1 ? prev : { ...prev, page: 1 })

  const [open, setOpen] = useState(null)
  const [note, setNote] = useState('')
  // ?issue=<uuid> opens that issue's dialog on arrival — Tim's lists link
  // each issue here (Satya, 2026-09-29: "click and go to that particular
  // issue"). The server's own permission rule decides: someone who may not
  // work the issue gets a 404 and simply lands on the board.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const uuid = params.get('issue')
    if (!uuid) return
    fetchIssue(uuid)
      .then((row) => {
        setOpen(row)
        setNote(row.resolution_note || '')
      })
      .catch(() => {})
  }, [])

  const [saving, setSaving] = useState(false)
  const [links, setLinks] = useState(null)
  const [sopOpen, setSopOpen] = useState(false)
  // A category move asks first: picking from the list only stages it
  // (Satya, 2026-09-28: "it happens instantly, we should have a pop up").
  const [pendingMove, setPendingMove] = useState(null)
  // Same for a status change (Satya, 2026-09-28): the list only stages it.
  const [pendingStatus, setPendingStatus] = useState(null)
  // The owner's own working notes on one issue (Satya, 2026-10-15: "a small
  // button to open a pop up for notes … internal, no Slack messaging"). The
  // row the pop-up is for, the notes fetched for it (null while loading),
  // and the one being typed.
  const [notesFor, setNotesFor] = useState(null)
  const [notes, setNotes] = useState(null)
  const [noteText, setNoteText] = useState('')
  const [noteSaving, setNoteSaving] = useState(false)
  const [noteError, setNoteError] = useState('')

  const centreName =
    centres.find((item) => item.uuid === centre)?.name || 'this centre'

  // The dialog follows the page scope rather than dumping every centre at
  // someone who is looking at one.
  const visibleLinks = (links || []).filter(
    (link) => centre === ALL_CENTRES || link.centre === centre
  )

  // Built here, not taken from the API, for the same reason Underage Consent
  // does it (UnderageConsent.js:125 `${window.location.origin}/consent/...`):
  // window.location.origin is whatever host you are already on, so a dev page
  // cannot hand out a prod link. The server-side `url` depends on a setting
  // that was wrong on dev and sent people to alfred.openhouse.study, where the
  // page is not deployed.
  const linkUrlFor = (link) =>
    `${window.location.origin}/report-issue/${link.token}`

  // Fill the window rather than guess at it: measure where the table actually
  // starts and give it the rest of the height, re-measuring on resize.
  const wrapRef = useRef(null)
  const footRef = useRef(null)
  const [wrapHeight, setWrapHeight] = useState(null)
  useEffect(() => {
    const measure = () => {
      if (!wrapRef.current) return
      const top = wrapRef.current.getBoundingClientRect().top
      // The pager under the table is always rendered, so its height is real
      // on the first measure and the table stops above it.
      const foot = footRef.current ? footRef.current.offsetHeight : 0
      // Subtract the page's own bottom padding as well as the gutter. Missing
      // it made the table ~48px taller than the space available, so the WHOLE
      // PAGE scrolled — title, centre picker and tabs sliding away — instead of
      // only the table body under its frozen header.
      const parent = wrapRef.current.parentElement
      const pad = parent
        ? parseFloat(window.getComputedStyle(parent).paddingBottom) || 0
        : 0
      setWrapHeight(
        Math.max(240, window.innerHeight - top - pad - foot - BOTTOM_GUTTER)
      )
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      const term = search.trim()
      setQuery((prev) => (prev.term === term ? prev : { term, page: 1 }))
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // Drives the empty-state wording: 'nothing matches' only makes sense
  // when something is actually filtering.
  const filtered = Boolean(query.term) || centre !== 'all' || category !== 'all'

  // Requests can overlap (type, then click a tab before the search returns);
  // only the latest one may touch the screen, or the older, slower answer
  // lands last and shows a page nobody asked for.
  const requestId = useRef(0)
  const load = useCallback(async () => {
    const id = ++requestId.current
    setLoading(true)
    try {
      const data = await fetchIssues({
        status: statusTab,
        centre,
        category,
        q: query.term,
        page: query.page,
        // Sorting moved to the server with pagination: sorting the 50 rows
        // on screen would order the page, not the board.
        sort: ageSort || undefined
      })
      if (id !== requestId.current) return
      setRows(Array.isArray(data?.results) ? data.results : [])
      setCounts(data?.counts || {})
      setOwnerScoped(Boolean(data?.owner_scoped))
      setMeta({
        total: data?.total || 0,
        page: data?.page || 1,
        pages: data?.pages || 1,
        size: data?.page_size || 50
      })
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }, [statusTab, centre, category, ageSort, query])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const loadOptions = async () => {
      const [cats, scoped] = await Promise.all([
        fetchIssueCategories(),
        fetchScopedCentres()
      ])
      setCategories(Array.isArray(cats) ? cats : [])
      const list = scoped?.centres || []
      setCentres(list)
      setUnrestricted(Boolean(scoped?.unrestricted))
      setCentre((prev) => {
        const allowed =
          prev &&
          ((prev === ALL_CENTRES && scoped?.unrestricted) ||
            list.some((c) => c.uuid === prev))
        if (allowed) return prev
        // A centre-mapped user has no "all centres" to fall back to.
        return scoped?.unrestricted ? ALL_CENTRES : list[0]?.uuid || ''
      })
    }
    loadOptions()
  }, [])

  useEffect(() => {
    if (centre) window.localStorage.setItem(CENTRE_KEY, centre)
  }, [centre])

  const move = async (row, nextStatus) => {
    setSaving(true)
    try {
      await updateIssue(row.uuid, {
        status: nextStatus,
        ...(nextStatus === 'resolved' ? { resolution_note: note } : {})
      })
      setOpen(null)
      setNote('')
      await load()
    } finally {
      setSaving(false)
    }
  }

  // Move an issue to another category. The API re-points the owner and, if
  // the issue has a Slack thread, replies there tagging the new owner.
  const refile = async (row, categoryUuid) => {
    if (!categoryUuid || categoryUuid === row.category) return
    setSaving(true)
    try {
      const updated = await updateIssue(row.uuid, { category: categoryUuid })
      setOpen(updated)
      await load()
    } finally {
      setSaving(false)
    }
  }

  const confirmMove = async () => {
    const move = pendingMove
    setPendingMove(null)
    if (move) await refile(open, move.uuid)
  }

  const openNotes = async (row) => {
    setNotesFor(row)
    setNotes(null)
    setNoteText('')
    setNoteError('')
    try {
      const data = await fetchIssueNotes(row.uuid)
      setNotes(Array.isArray(data?.notes) ? data.notes : [])
    } catch (error) {
      setNotes([])
      setNoteError('Could not load the notes. Close and try again.')
    }
  }

  const closeNotes = () => {
    setNotesFor(null)
    setNotes(null)
    setNoteText('')
    setNoteError('')
  }

  // The count on the row's button and in the open issue, kept in step with
  // what was just saved — without refetching the whole page.
  const bumpNotesCount = (uuid, count) => {
    const stamp = (row) =>
      row && row.uuid === uuid ? { ...row, notes_count: count } : row
    setRows((prev) => prev.map(stamp))
    setOpen(stamp)
    setNotesFor(stamp)
  }

  const addNote = async () => {
    const text = noteText.trim()
    if (!text || !notesFor) return
    setNoteSaving(true)
    setNoteError('')
    try {
      const data = await addIssueNote(notesFor.uuid, text)
      const list = Array.isArray(data?.notes) ? data.notes : []
      setNotes(list)
      setNoteText('')
      bumpNotesCount(notesFor.uuid, list.length)
    } catch (error) {
      setNoteError(
        error?.response?.data?.detail || 'Could not save the note. Try again.'
      )
    } finally {
      setNoteSaving(false)
    }
  }

  const noteWhen = (iso) =>
    iso
      ? new Date(iso).toLocaleString('en-IN', {
          day: 'numeric',
          month: 'short',
          hour: 'numeric',
          minute: '2-digit'
        })
      : ''

  // ONE step, not two (Satya, 2026-09-22, centre item a; his 09-23 clip
  // showed the two: "Centre form links" -> dialog -> click the link). With a
  // centre picked, the button opens that centre's form straight away. Only
  // "all centres" — which just multi-centre HQ users have — still lists the
  // links, because there is no one centre to open.
  const raiseIssue = async () => {
    const data = await fetchIssueFormLinks()
    const list = Array.isArray(data) ? data : []
    const mine = centre !== ALL_CENTRES && list.find((l) => l.centre === centre)
    if (mine) {
      window.open(linkUrlFor(mine), '_blank', 'noopener')
      return
    }
    setLinks(list)
  }

  // The Notes column is the owner's: a centre-tagged reader, whose rows
  // all come back can_resolve=false, gets no column at all — not even an
  // empty one to wonder about (Satya, 2026-10-15: "I did not want the
  // centre teams to see this").
  const showNotes = rows.some((row) => row.can_resolve)

  const rangeFrom = (meta.page - 1) * meta.size + 1
  const rangeTo = Math.min(meta.page * meta.size, meta.total)

  return (
    <div className={classes.root}>
      <Box className={classes.head}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5">Issue Tracker</Typography>
          <Tooltip title="how this works">
            <IconButton size="small" onClick={() => setSopOpen(true)}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box className={classes.filters}>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.centre}
          >
            <InputLabel>centre</InputLabel>
            <Select
              label="centre"
              value={centre}
              onChange={(event) => {
                setCentre(event.target.value)
                setQuery(firstPage)
              }}
            >
              {unrestricted && (
                <MenuItem value={ALL_CENTRES}>all centres</MenuItem>
              )}
              {centres.map((item) => (
                <MenuItem key={item.uuid} value={item.uuid}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" color="primary" onClick={raiseIssue}>
            Raise an issue
          </Button>
        </Box>
      </Box>

      <div className={classes.bar}>
        {STATUS_TABS.map((tab) => (
          <div
            key={tab.value}
            role="button"
            tabIndex={0}
            className={`${classes.tab} ${
              statusTab === tab.value ? classes.tabOn : classes.tabOff
            }`}
            onClick={() => {
              setStatusTab(tab.value)
              setQuery(firstPage)
            }}
            onKeyPress={() => {
              setStatusTab(tab.value)
              setQuery(firstPage)
            }}
          >
            {tab.label}
            <span className={classes.count}>{counts[tab.countKey] ?? 0}</span>
          </div>
        ))}
      </div>

      {/* The table always renders, even with no rows: the filter row lives in
          its header, and hiding it on an empty result would strand you with a
          search you could no longer clear. */}
      {
        <TableContainer
          ref={wrapRef}
          className={classes.tableWrap}
          style={wrapHeight ? { maxHeight: wrapHeight } : undefined}
        >
          <Table size="small" stickyHeader className={classes.table}>
            <TableHead>
              <TableRow className={classes.labelRow}>
                <TableCell className={classes.colIssue}>Issue</TableCell>
                <TableCell className={classes.colCentre}>Centre</TableCell>
                <TableCell className={classes.colCategory}>
                  Category · owner
                </TableCell>
                <TableCell className={classes.colStatus}>Status</TableCell>
                <TableCell className={classes.colAge}>
                  <TableSortLabel
                    active={!!ageSort}
                    direction={ageSort === 'oldest' ? 'desc' : 'asc'}
                    onClick={() => {
                      setAgeSort(ageSort === 'oldest' ? 'newest' : 'oldest')
                      setQuery(firstPage)
                    }}
                  >
                    Age
                  </TableSortLabel>
                </TableCell>
                {showNotes ? (
                  <TableCell className={classes.colNotes}>Notes</TableCell>
                ) : null}
              </TableRow>
              {/* Filter row. stickyHeader pins every head cell to top:0, so the
                  second row has to be pushed down by the height of the first or
                  the two stack on top of each other. */}
              <TableRow className={classes.filterRow}>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    className={classes.headFilter}
                    placeholder="search…"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="disabled" />
                        </InputAdornment>
                      )
                    }}
                  />
                </TableCell>
                {/* Centre is filtered by the picker in the page header */}
                <TableCell />
                <TableCell>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    variant="outlined"
                    className={classes.headFilter}
                    value={category}
                    onChange={(event) => {
                      setCategory(event.target.value)
                      setQuery(firstPage)
                    }}
                  >
                    <MenuItem value="all">all categories</MenuItem>
                    {categories
                      .filter((item) => item.active || item.uuid === category)
                      .map((item) => (
                        <MenuItem key={item.uuid} value={item.uuid}>
                          {item.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </TableCell>
                {/* Status is the tab strip above; Age and Notes have
                    nothing to filter on */}
                <TableCell />
                <TableCell />
                {showNotes ? <TableCell /> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={showNotes ? 6 : 5}
                    className={classes.empty}
                  >
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={showNotes ? 6 : 5}
                    className={classes.empty}
                  >
                    {/* Three different empty boards, and telling them apart
                        matters: filters hiding everything, an owner whose own
                        queues are quiet, and a genuinely empty tracker. Saying
                        'no issues yet' to the middle one is simply false. */}
                    {filtered
                      ? 'nothing matches those filters.'
                      : ownerScoped
                      ? 'nothing here for the categories you own. Issues reach you by category — a Super Admin sets who owns what, at HQ > Issue Tracker > Issue Owners.'
                      : 'no issues yet.'}
                  </TableCell>
                </TableRow>
              ) : null}
              {rows.map((row) => (
                <TableRow
                  key={row.uuid}
                  hover
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setOpen(row)
                    setNote(row.resolution_note || '')
                  }}
                >
                  <TableCell className={classes.wrap}>
                    <span className={classes.title}>{row.title}</span>
                    <span className={classes.sub}>
                      {row.reference} · {row.raised_by_name} ·{' '}
                      {row.source_label}
                    </span>
                  </TableCell>
                  <TableCell className={classes.wrap}>
                    {row.centre_name}
                  </TableCell>
                  <TableCell className={classes.wrap}>
                    {row.category_name || '—'}
                    <br />
                    <span className={classes.sub}>{row.owner_name}</span>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      className={classes.chip}
                      label={row.status_label}
                      style={STATUS_STYLE[row.status]}
                    />
                    {row.centre_confirmation ? (
                      <span className={classes.confirm}>
                        {CONFIRM_SHORT[row.centre_confirmation]}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell
                    className={`${classes.num} ${
                      row.status !== 'resolved' && row.age_days >= STALE_DAYS
                        ? classes.stale
                        : ''
                    }`}
                  >
                    {row.age_days}d
                  </TableCell>
                  {showNotes ? (
                    <TableCell className={classes.num}>
                      {/* Owner-only working notes. Its own click, or the row
                          underneath would open the issue as well. A row this
                          person may not work gets an empty cell. */}
                      {row.can_resolve ? (
                        <Tooltip title="Your notes — kept in Alfred, never posted to Slack, not shown to the centre">
                          <Button
                            size="small"
                            variant="outlined"
                            className={classes.notesBtn}
                            startIcon={<NotesOutlinedIcon fontSize="small" />}
                            onClick={(event) => {
                              event.stopPropagation()
                              openNotes(row)
                            }}
                          >
                            {row.notes_count || 0}
                          </Button>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      }

      {/* 50 a page. The caption and the pager read `meta` — what the server
          served — so a clamped page shows as the page it really is. The pager
          only appears when there is a second page to go to. */}
      <div ref={footRef} className={classes.foot}>
        <span className={classes.sub}>
          {meta.total > 0
            ? `${rangeFrom}–${rangeTo} of ${meta.total.toLocaleString('en-IN')}`
            : ''}
        </span>
        {meta.pages > 1 ? (
          <Pagination
            count={meta.pages}
            page={meta.page}
            onChange={(event, value) =>
              setQuery((prev) => ({ ...prev, page: value }))
            }
            size="small"
            shape="rounded"
          />
        ) : null}
      </div>

      {/* One issue */}
      <Dialog
        open={Boolean(open)}
        onClose={() => setOpen(null)}
        maxWidth="sm"
        fullWidth
      >
        {open ? (
          <>
            <DialogTitle>{open.title}</DialogTitle>
            <DialogContent dividers>
              <Typography className={classes.sub}>
                {open.reference} · raised by {open.raised_by_name}
                {open.raised_by_email ? ` (${open.raised_by_email})` : ''}
              </Typography>
              <div className={classes.body}>{open.description}</div>

              <div className={classes.dl}>
                <span className={classes.dt}>Centre</span>
                <span>{open.centre_name}</span>
                <span className={classes.dt}>Category</span>
                <span>
                  {open.can_resolve && open.status !== 'resolved' ? (
                    /* Filed under the wrong thing? Move it. The owner
                       follows the category, and the new owner is tagged in
                       the issue's own Slack thread (2026-09-22, HQ item f). */
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      className={classes.dialogField}
                      SelectProps={{ native: true }}
                      disabled={saving}
                      value={open.category || ''}
                      onChange={(event) => {
                        const target = categories.find(
                          (item) => item.uuid === event.target.value
                        )
                        if (target && target.uuid !== open.category) {
                          setPendingMove(target)
                        }
                      }}
                    >
                      {categories
                        .filter(
                          (item) => item.active || item.uuid === open.category
                        )
                        .map((item) => (
                          <option key={item.uuid} value={item.uuid}>
                            {item.name}
                          </option>
                        ))}
                    </TextField>
                  ) : (
                    open.category_name || '—'
                  )}
                </span>
                <span className={classes.dt}>Owner</span>
                <span>{open.owner_name || '—'}</span>
                <span className={classes.dt}>Status</span>
                <span>
                  {open.can_resolve ? (
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      className={classes.dialogField}
                      SelectProps={{ native: true }}
                      disabled={saving}
                      value={open.status}
                      onChange={(event) => {
                        const target = STATUS_OPTIONS.find(
                          (item) => item.value === event.target.value
                        )
                        if (target && target.value !== open.status) {
                          setPendingStatus(target)
                        }
                      }}
                    >
                      {STATUS_OPTIONS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </TextField>
                  ) : (
                    open.status_label
                  )}
                </span>
                <span className={classes.dt}>Seen in</span>
                <span>{open.source_other || open.source_label}</span>
                {open.about_name || open.about_number ? (
                  <>
                    <span className={classes.dt}>About</span>
                    <span>
                      {[open.about_label, open.about_name, open.about_number]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </>
                ) : null}
                <span className={classes.dt}>Open for</span>
                <span>{open.age_days} days</span>
                {/* The id the Google Sheet knew this by — which is this row's
                    own primary key, because the bridge imports with
                    `uuid = UUID(row['Issue Id'])`. Shown so it can be pasted
                    into Slack search to find the original thread; only rows
                    that actually came from the sheet carry it. */}
                {open.old_tracker_id ? (
                  <>
                    <span className={classes.dt}>Old tracker ID</span>
                    <span className={classes.oldId}>{open.old_tracker_id}</span>
                  </>
                ) : null}
                {open.resolved_by_name ? (
                  <>
                    <span className={classes.dt}>Resolved by</span>
                    <span>{open.resolved_by_name}</span>
                  </>
                ) : null}
                {/* The centre's word on the fix, read from the Slack thread
                    after the resolution reply asked for it. */}
                {open.centre_confirmation ? (
                  <>
                    <span className={classes.dt}>Centre says</span>
                    <span>
                      {open.centre_confirmation_label}
                      {open.centre_confirmation_note ? (
                        <span className={classes.sub}>
                          {' '}
                          · “{open.centre_confirmation_note}”
                        </span>
                      ) : null}
                    </span>
                  </>
                ) : null}
              </div>

              {(open.screenshots || []).length ? (
                <Box mt={2}>
                  {open.screenshots.map((shot) => (
                    <div key={shot.url}>
                      <Link
                        href={shot.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {shot.name || 'screenshot'}
                      </Link>
                    </div>
                  ))}
                </Box>
              ) : null}

              {open.slack_permalink ? (
                <Box mt={2}>
                  <Link
                    href={open.slack_permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open the Slack thread
                  </Link>
                </Box>
              ) : null}

              {open.slack_error ? (
                <div className={classes.slackErr}>
                  This one never reached Slack: {open.slack_error}
                </div>
              ) : null}

              {open.status !== 'resolved' && open.can_resolve ? (
                <Box mt={2.5}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    variant="outlined"
                    label="What fixed it — saved with your name and posted in the Slack thread when you resolve"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                  />
                </Box>
              ) : null}

              {/* Say who CAN work it rather than leaving missing buttons
                  unexplained. `can_resolve` is the server's own answer, from
                  the same rule the PATCH enforces — it now covers picking up
                  and reopening too, not only closing. */}
              {open.status !== 'resolved' && !open.can_resolve ? (
                <Box mt={2.5}>
                  <Typography className={classes.sub}>
                    {open.owner_name
                      ? `This one is ${open.owner_name}'s to work — they own ${open.category_name} — or a Super Admin's.`
                      : "This one is the category owner's to work, or a Super Admin's."}
                  </Typography>
                </Box>
              ) : null}
            </DialogContent>
            <DialogActions>
              {open.can_resolve ? (
                <Button
                  startIcon={<NotesOutlinedIcon fontSize="small" />}
                  onClick={() => openNotes(open)}
                >
                  Notes · {open.notes_count || 0}
                </Button>
              ) : null}
              <Button onClick={() => setOpen(null)}>Close</Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {/* Working notes on one issue. Internal on purpose: the API behind
          this writes a row and nothing else — no Slack reply, no Tim, no
          nudge — so an owner can log "called the vendor, waiting" without
          it reaching the centre. Owner-only both ways: the button only
          renders on `can_resolve`, and the API 404s anyone else, so a
          centre-tagged reader never sees that notes exist. */}
      <Dialog
        open={Boolean(notesFor)}
        onClose={closeNotes}
        maxWidth="sm"
        fullWidth
      >
        {notesFor ? (
          <>
            <DialogTitle>Notes · {notesFor.reference}</DialogTitle>
            <DialogContent dividers>
              <Typography className={classes.sub} style={{ marginBottom: 12 }}>
                {notesFor.title}
                <br />
                For you to track what was done and what is pending. Only the
                category owner and Super Admins see these — never the centre,
                never Slack.
              </Typography>

              {notes === null ? (
                <Box py={2} textAlign="center">
                  <CircularProgress size={22} />
                </Box>
              ) : notes.length === 0 ? (
                <Typography className={classes.sub}>No notes yet.</Typography>
              ) : (
                notes.map((item) => (
                  <div key={item.uuid} className={classes.noteRow}>
                    <Typography className={classes.sub}>
                      {item.author_name} · {noteWhen(item.created)}
                    </Typography>
                    <Typography className={classes.noteText}>
                      {item.text}
                    </Typography>
                  </div>
                ))
              )}

              <Box mt={2.5}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  variant="outlined"
                  label="Add a note — what you did, or what is pending"
                  value={noteText}
                  disabled={noteSaving}
                  onChange={(event) => setNoteText(event.target.value)}
                />
              </Box>

              {noteError ? (
                <div className={classes.slackErr}>{noteError}</div>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeNotes}>Close</Button>
              <Button
                color="primary"
                variant="contained"
                disabled={!noteText.trim() || noteSaving}
                onClick={addNote}
              >
                {noteSaving ? 'Saving…' : 'Add note'}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {/* The per-centre form links, to send to a centre. Scoped to whatever
          the page is scoped to: on a single centre you get that centre's link
          and nothing else to copy by mistake; on "all centres" — which only
          a multi-centre user has — you get the full list. */}
      <Dialog
        open={Boolean(links)}
        onClose={() => setLinks(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {centre === ALL_CENTRES
            ? 'Pick a centre'
            : `Form link — ${centreName}`}
        </DialogTitle>
        <DialogContent dividers>
          <Typography className={classes.sub} style={{ marginBottom: 12 }}>
            One permanent link per centre. It carries the centre, so nobody has
            to pick it; raising needs an Alfred login.
          </Typography>
          {visibleLinks.length === 0 ? (
            <Typography className={classes.sub}>
              No link for this centre yet.
            </Typography>
          ) : null}
          {visibleLinks.map((link) => (
            <Box key={link.uuid} mb={2}>
              <div style={{ fontWeight: 600 }}>{link.centre_name}</div>
              <Link
                href={linkUrlFor(link)}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.linkUrl}
              >
                {linkUrlFor(link)}
              </Link>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinks(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Status change — confirm before it saves. Resolving also replies in
          the Slack thread and asks the centre to confirm, so say so. */}
      <Dialog
        open={Boolean(pendingStatus && open)}
        onClose={() => setPendingStatus(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change the status?</DialogTitle>
        <DialogContent dividers>
          {pendingStatus && open ? (
            <>
              <Typography paragraph={pendingStatus.value === 'resolved'}>
                From <strong>{open.status_label}</strong> to{' '}
                <strong>{pendingStatus.label}</strong>.
              </Typography>
              {pendingStatus.value === 'resolved' ? (
                <Typography className={classes.sub}>
                  {note.trim()
                    ? 'What fixed it is saved with your name.'
                    : 'You have not written what fixed it yet.'}
                  {open.slack_permalink
                    ? note.trim()
                      ? ' It also goes in the Slack thread with a Resolved message, asking the centre to confirm.'
                      : ' A Resolved message goes in its Slack thread, asking the centre to confirm.'
                    : ''}
                </Typography>
              ) : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingStatus(null)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              const next = pendingStatus
              setPendingStatus(null)
              if (next) move(open, next.value)
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category move — confirm before it saves, since it re-points the
          owner and replies in the centre's Slack thread. */}
      <Dialog
        open={Boolean(pendingMove && open)}
        onClose={() => setPendingMove(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Move this issue?</DialogTitle>
        <DialogContent dividers>
          {pendingMove && open ? (
            <Typography>
              From <strong>{open.category_name || '—'}</strong> to{' '}
              <strong>{pendingMove.name}</strong>. It will be with{' '}
              <strong>{pendingMove.owner_name || 'no owner yet'}</strong>
              {open.slack_permalink
                ? ', and a message in its Slack thread will say it was moved and tag them.'
                : '.'}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingMove(null)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={confirmMove}>
            Move
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tim's "Connect my Alfred account" result — see the effect above */}
      <Dialog
        open={Boolean(timConnect)}
        onClose={() => setTimConnect(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Connect to Tim</DialogTitle>
        <DialogContent>
          {timConnect?.state === 'working' && <CircularProgress size={24} />}
          {timConnect?.state === 'done' && (
            <Typography>
              Connected{timConnect.name ? `, ${timConnect.name}` : ''}. Tim now
              knows who you are — go back to Slack and ask him what&apos;s open
              with you.
            </Typography>
          )}
          {timConnect?.state === 'error' && (
            <Typography color="error">{timConnect.text}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTimConnect(null)}
            disabled={timConnect?.state === 'working'}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* How this works — the ⓘ next to the title, same as Incident Reports */}
      <Dialog
        open={sopOpen}
        onClose={() => setSopOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>How the Issue Tracker works</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph>
            Every centre has one permanent link to its form. They bookmark it
            once; the link carries the centre, so it cannot be mis-picked, and
            the raiser&apos;s name comes from their Alfred login.{' '}
            <strong>Raise an issue</strong> above opens it for the centre picked
            in the header.
          </Typography>
          <Typography paragraph>
            Raising an issue posts it to that centre&apos;s own Slack channel
            and tags whoever owns the category, exactly as the old &quot;Track
            that Issue&quot; app did. The 13 categories each have one owner, so
            nothing lands in a queue with no name on it.
          </Typography>
          <Typography paragraph>
            Resolving an issue here replies in that same Slack thread, so the
            conversation and the status stay in one place — the old sheet
            drifted because the status was typed by hand. Moving an issue to
            another category tags the new owner in that thread too, and an issue
            nobody has picked up a day later gets its owner tagged once.
          </Typography>
          <Typography className={classes.sub}>
            Anything open longer than {STALE_DAYS} days shows its age in red.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSopOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default IssueTracker
